import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useApp } from '../context/AppContext'
import { canAccessPath, isAdmin } from '../lib/accessControl'
import { countAprovacoesPendentesVisiveis } from '../lib/aprovacaoVisibility'
import {
  NAVIGATION,
  findGroupForPath,
  type NavEntry,
  type NavGroupEntry,
  type NavLinkEntry,
  type NavSection,
} from '../lib/navigation'
import SidebarIcon, { ChevronIcon } from './SidebarIcon'

interface ResolvedLink {
  kind: 'link'
  id: string
  label: string
  icon: NavEntry['icon']
  path: string
}

interface ResolvedGroup {
  kind: 'group'
  id: string
  label: string
  icon: NavEntry['icon']
  badge?: 'aprovacoes'
  children: { label: string; path: string }[]
}

type ResolvedEntry = ResolvedLink | ResolvedGroup

export default function Layout({ children }: { children: React.ReactNode }) {
  const { currentUser, logout, aprovacoes } = useApp()
  const navigate = useNavigate()
  const location = useLocation()

  const [expanded, setExpanded] = useState<string | null>(() => findGroupForPath(location.pathname))
  const [collapsed, setCollapsed] = useState(false)

  const pendentes = countAprovacoesPendentesVisiveis(aprovacoes, currentUser)
  const admin = isAdmin(currentUser?.perfil)

  const isLink = (entry: NavEntry): entry is NavLinkEntry => 'path' in entry
  const isGroup = (entry: NavEntry): entry is NavGroupEntry => 'children' in entry

  function buildSection(section: NavSection): ResolvedEntry[] {
    return NAVIGATION.filter(entry => entry.section === section)
      .filter(entry => (entry.adminOnly ? admin : true))
      .map(entry => {
        if (isLink(entry)) {
          return canAccessPath(currentUser?.perfil, entry.path)
            ? { kind: 'link' as const, id: entry.id, label: entry.label, icon: entry.icon, path: entry.path }
            : null
        }

        const children = (entry.children ?? []).filter(child =>
          canAccessPath(currentUser?.perfil, child.path),
        )
        if (children.length === 0) return null

        return {
          kind: 'group' as const,
          id: entry.id,
          label: entry.label,
          icon: entry.icon,
          badge: entry.badge,
          children,
        }
      })
      .filter((entry): entry is ResolvedEntry => entry !== null)
  }

  const operacional = buildSection('operacional')
  const administrativo = buildSection('administrativo')

  // O grupo da rota ativa abre automaticamente ao carregar e a cada navegacao.
  useEffect(() => {
    const group = findGroupForPath(location.pathname)
    if (group) setExpanded(group)
  }, [location.pathname])

  function toggleGroup(id: string) {
    setExpanded(prev => (prev === id ? null : id))
  }

  const isActive = (path: string) => location.pathname === path

  function renderEntry(entry: ResolvedEntry) {
    if (entry.kind === 'link') {
      const active = isActive(entry.path)
      return (
        <button
          key={entry.id}
          type="button"
          onClick={() => navigate(entry.path)}
          className={`sidebar-item${active ? ' sidebar-item-active' : ''}`}
          aria-current={active ? 'page' : undefined}
          title={collapsed ? entry.label : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: collapsed ? '9px 0' : '9px 14px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: active ? '#f7f8f8' : '#8a8f98',
            fontSize: 13,
            fontWeight: active ? 500 : 400,
            textAlign: 'left',
          }}
        >
          <SidebarIcon name={entry.icon} size={16} color={active ? '#f0403b' : '#62666d'} />
          {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{entry.label}</span>}
        </button>
      )
    }

    const open = expanded === entry.id
    const hasActiveChild = entry.children.some(child => isActive(child.path))
    const badgeCount = entry.badge === 'aprovacoes' ? pendentes : 0

    return (
      <div key={entry.id}>
        <button
          type="button"
          onClick={() => (collapsed ? setCollapsed(false) : toggleGroup(entry.id))}
          className={`sidebar-item${hasActiveChild ? ' sidebar-group-active' : ''}`}
          aria-expanded={open}
          title={collapsed ? entry.label : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            width: '100%',
            padding: collapsed ? '9px 0' : '9px 14px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            justifyContent: collapsed ? 'center' : 'flex-start',
            color: hasActiveChild ? '#f7f8f8' : '#8a8f98',
            fontSize: 13,
            fontWeight: hasActiveChild ? 500 : 400,
            textAlign: 'left',
          }}
        >
          <SidebarIcon name={entry.icon} size={16} color={hasActiveChild ? '#f0403b' : '#62666d'} />

          {!collapsed && (
            <>
              <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{entry.label}</span>

              {badgeCount > 0 && (
                <span
                  style={{
                    minWidth: 18,
                    height: 18,
                    padding: '0 5px',
                    borderRadius: 9,
                    background: '#f0403b',
                    color: '#fff',
                    fontSize: 10.5,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                  aria-label={`${badgeCount} aprovações pendentes`}
                >
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}

              <ChevronIcon open={open} size={12} color="#62666d" />
            </>
          )}
        </button>

        {!collapsed && open && (
          <div style={{ paddingBottom: 4 }}>
            {entry.children.map(child => {
              const active = isActive(child.path)
              return (
                <button
                  key={child.path}
                  type="button"
                  onClick={() => navigate(child.path)}
                  className={`sidebar-item${active ? ' sidebar-item-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '7px 14px 7px 40px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: active ? '#f7f8f8' : '#8a8f98',
                    fontSize: 12.5,
                    fontWeight: active ? 500 : 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {child.label}
                </button>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0f1011' }}>
      <aside
        style={{
          width: collapsed ? 56 : 232,
          minWidth: collapsed ? 56 : 232,
          background: '#13141a',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s, min-width 0.2s',
          overflow: 'hidden',
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? '16px 0' : '16px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            minHeight: 58,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
            }}
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <img
              src="https://static.lumi.new/material/1f/1f027f739bb812c447d651aef7dfaf6f.png"
              alt="4S Engenharia"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </div>
          {!collapsed && (
            <div style={{ minWidth: 0 }}>
              <p style={{ color: '#f7f8f8', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                Núcleo Financeiro
              </p>
              <p style={{ color: '#62666d', fontSize: 10.5, whiteSpace: 'nowrap' }}>4S Engenharia</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 0 12px', overflowY: 'auto' }}>
          {operacional.map(renderEntry)}

          {operacional.length > 0 && administrativo.length > 0 && (
            <div
              role="separator"
              aria-orientation="horizontal"
              style={{
                margin: collapsed ? '12px 12px' : '14px 14px 12px',
                borderTop: '1px solid rgba(255,255,255,0.08)',
              }}
            />
          )}

          {administrativo.map(renderEntry)}
        </nav>

        {/* User */}
        <div
          style={{
            padding: collapsed ? '12px 0' : '12px 14px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#f0403b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
              {currentUser?.nome?.charAt(0) || 'A'}
            </span>
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  color: '#f7f8f8',
                  fontSize: 12,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {currentUser?.nome}
              </p>
              <p style={{ color: '#62666d', fontSize: 11 }}>{currentUser?.perfil}</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={logout}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#62666d',
                fontSize: 12,
                padding: 4,
              }}
              title="Sair"
              aria-label="Sair"
            >
              ⏻
            </button>
          )}
        </div>
      </aside>

      <main style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        <div
          style={{
            height: 48,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            gap: 16,
            position: 'sticky',
            top: 0,
            background: '#0f1011',
            zIndex: 10,
          }}
        >
          <div style={{ flex: 1 }} />
          {pendentes > 0 && canAccessPath(currentUser?.perfil, '/aprovacoes') && (
            <button
              onClick={() => navigate('/aprovacoes')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 6,
                padding: '4px 10px',
                cursor: 'pointer',
                color: '#f87171',
                fontSize: 12,
              }}
            >
              {pendentes} {pendentes > 1 ? 'aprovações pendentes' : 'aprovação pendente'}
            </button>
          )}
          <span style={{ color: '#62666d', fontSize: 12 }}>{currentUser?.perfil}</span>
        </div>
        <div style={{ padding: '24px', minHeight: 'calc(100vh - 48px)' }}>{children}</div>
      </main>
    </div>
  )
}
